import { Star } from "lucide-react";

/**
 * GoogleRatingBadge — Display verified Google Places rating + review count.
 * Use only when the entity has google_rating + google_review_count fields.
 *
 * Props:
 *  - rating (number 0-5)
 *  - count (int)
 *  - placeId (optional string for Maps deep link)
 *  - variant: "light" | "dark"
 */
export default function GoogleRatingBadge({ rating, count, placeId, variant = "light", className = "" }) {
    if (rating == null) return null;

    const isDark = variant === "dark";
    const linkBase = "https://www.google.com/maps";
    const href = placeId
        ? `${linkBase}/place/?q=place_id:${placeId}`
        : `${linkBase}/search/?api=1&query=${encodeURIComponent(rating + " " + (count || 0))}`;

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="google-rating-badge"
            className={`inline-flex items-center gap-2 px-3 py-1.5 border transition-colors group ${
                isDark
                    ? "border-cp-ivoire/30 text-cp-ivoire/80 hover:bg-cp-ivoire/10"
                    : "border-cp-border text-cp-encre hover:bg-cp-craie"
            } ${className}`}
            title={`Note Google · ${rating}/5 sur ${count || 0} avis vérifiés`}
        >
            <Star size={13} strokeWidth={1.5} fill="currentColor" className="text-amber-500" />
            <span className="cp-serif text-base font-medium tabular-nums">{rating.toFixed(1)}</span>
            <span className="cp-tiny opacity-60">/5</span>
            {count != null && (
                <>
                    <span className="opacity-30">·</span>
                    <span className="cp-tiny opacity-70 tabular-nums">{count.toLocaleString("fr-FR")} avis</span>
                </>
            )}
            <span className="cp-tiny opacity-50 ml-1 group-hover:opacity-100 transition-opacity">Google</span>
        </a>
    );
}
